import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import { BRAND } from "@/lib/brand";
import { POSTS, getPost } from "@/lib/blog";
import { ArrowLeft, Clock, ArrowRight, MessageCircle, Phone } from "lucide-react";

function renderBody(body) {
  const blocks = body.split(/\n\n+/);
  return blocks.map((block, i) => {
    if (block.startsWith("## ")) {
      return (
        <h2 key={i} className="font-heading font-bold text-2xl mt-10 mb-3">
          {block.replace(/^##\s+/, "")}
        </h2>
      );
    }
    if (block.split("\n").every((l) => l.trim().startsWith("- "))) {
      return (
        <ul key={i} className="my-4 space-y-2 list-disc pl-5">
          {block.split("\n").map((l, j) => (
            <li key={j} className="text-slate-700 leading-relaxed">{l.replace(/^-\s+/, "")}</li>
          ))}
        </ul>
      );
    }
    if (/^\d+\.\s/.test(block.split("\n")[0])) {
      return (
        <ol key={i} className="my-4 space-y-2 list-decimal pl-5">
          {block.split("\n").map((l, j) => (
            <li key={j} className="text-slate-700 leading-relaxed">{l.replace(/^\d+\.\s+/, "")}</li>
          ))}
        </ol>
      );
    }
    return <p key={i} className="my-4 text-slate-700 leading-relaxed text-[16.5px]">{block}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  useEffect(() => {
    if (post) document.title = `${post.title} — ${BRAND.name}`;
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = POSTS.filter((p) => p.slug !== post.slug && p.category === post.category).slice(0, 3);
  const idx = POSTS.findIndex((p) => p.slug === post.slug);
  const next = POSTS[(idx + 1) % POSTS.length];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />

      <article className="max-w-[820px] mx-auto px-5 md:px-8 py-12">
        <Link to="/blog" data-testid="post-back-to-blog" className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-emerald-brand inline-flex items-center gap-2">
          <ArrowLeft className="w-3 h-3" /> Back to blog
        </Link>

        <div className="mt-6 text-[11px] uppercase tracking-[0.18em] text-emerald-brand font-semibold">{post.category}</div>
        <h1 className="font-heading font-bold text-3xl md:text-5xl mt-3 leading-tight">{post.title}</h1>
        <div className="mt-5 flex items-center gap-5 text-xs text-slate-500">
          <span>By {post.author}</span>
          <span>{new Date(post.date).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "2-digit" })}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.readTime}</span>
        </div>

        <div className="mt-8 rounded-[24px] overflow-hidden brand-shadow aspect-[16/9]">
          <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <div className="mt-10 text-slate-700">
          {renderBody(post.body)}
        </div>

        {/* In-post CTA */}
        <div className="mt-12 rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#11365E] text-white p-8 md:p-10">
          <h3 className="font-heading font-bold text-2xl">Have a case like this?</h3>
          <p className="mt-2 text-slate-300 text-sm max-w-xl">
            Send us your documents and a senior advisor will review your case within 24 working hours. First consultation is free.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="post-cta-contact" className="btn-gold text-sm">
              Book a free review <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" data-testid="post-cta-whatsapp" className="btn-emerald text-sm">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <a href={BRAND.telUrl} data-testid="post-cta-call" className="px-5 py-3 rounded-full border border-white/20 text-white hover:bg-white/10 text-sm font-semibold inline-flex items-center gap-2">
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
          </div>
        </div>

        {/* Related / next */}
        {related.length > 0 && (
          <div className="mt-16">
            <div className="eyebrow">Continue reading</div>
            <h3 className="font-heading font-bold text-2xl mt-3">More in {post.category}</h3>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="brand-card overflow-hidden block">
                  <div className="aspect-[16/10]"><img src={r.cover} alt={r.title} className="w-full h-full object-cover" /></div>
                  <div className="p-5">
                    <div className="font-heading font-semibold text-sm leading-snug line-clamp-2">{r.title}</div>
                    <div className="text-[11px] text-slate-500 mt-2 inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {r.readTime}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 border-t border-slate-200 pt-6 flex items-center justify-between">
          <Link to="/blog" className="text-sm font-semibold text-slate-600 hover:text-emerald-brand inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
          <Link to={`/blog/${next.slug}`} className="text-sm font-semibold text-navy hover:text-emerald-brand inline-flex items-center gap-1.5">
            Next: {next.title.slice(0, 36)}{next.title.length > 36 ? "…" : ""} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </article>

      <SiteFooter />
      <WhatsAppFAB />
    </div>
  );
}
